import { deriveValidatorScore } from "./score-derivation";
import {
  DEMO_RESULT_KIND,
  type DemoGeneratedIdea,
  type DemoIdealCustomer,
  type DemoPreview,
  type DemoPreviewPainPoint,
} from "./types";
import { NICHE_CATALOG } from "./niche-catalog";

export type GuidedDemoNicheOption = (typeof NICHE_CATALOG)[number];

/**
 * Aligned starter niches — exactly the 7 canonical /test-ai slugs.
 * No translation layer needed; GUIDED_DEMO_STARTER_NICHES === NICHE_CATALOG.
 */
export const GUIDED_DEMO_STARTER_NICHES: readonly GuidedDemoNicheOption[] =
  NICHE_CATALOG;

/**
 * Default preview shown in the guided demo landing.
 *
 * Purpose:
 * - Give visitors a fast "aha" moment with believable output
 * - Keep friction low by showing value before lead capture
 */
export const GUIDED_DEMO_SAMPLE_PREVIEW: DemoPreview = {
  nicheSlug: "idea-validation",
  timeWindowDays: 30,
  validationLevel: "MVP",
  consistencyScore: 0.74,
  summary:
    "Across 2,486 public conversations, founders repeatedly struggle to turn customer feedback into clear, prioritized roadmap decisions.",
  methodology:
    "Signals were clustered from Reddit conversations over the last 30 days, then ranked by severity and repetition.",
  confidenceDisclaimer:
    "Preview data is anonymized and directional. Validate with your own customer interviews before committing roadmap bets.",
  postsCollected: 20,
  painPoints: [
    {
      id: "pp-feedback-overload",
      title: "Feedback is scattered across too many channels",
      description:
        "Founders collect input in Discord, support inboxes, and social threads, but cannot merge it into one decision-ready view.",
      severityScore: 80,
      frequency: 10,
      category: "tool_complaint",
      evidence: [
        {
          id: "ev-fo-1",
          text: "I have feedback in Intercom, Reddit, and Notion. I still cannot answer what to build next.",
          author: "u/indiefounder90",
          community: "r/SaaS",
          platform: "reddit",
          sourceLabel: "Reddit",
          sourceUrl:
            "https://www.reddit.com/r/SaaS/comments/1cs9qsw/how_do_you_prioritize_feature_requests_when/",
          permalink:
            "https://www.reddit.com/r/SaaS/comments/1cs9qsw/how_do_you_prioritize_feature_requests_when/",
        },
        {
          id: "ev-fo-3",
          text: "Customer requests are spread across Discord, support tickets, and random DMs, so roadmap planning turns into copy-paste chaos.",
          author: "u/buildstackdaily",
          community: "r/startups",
          platform: "reddit",
          sourceLabel: "Reddit",
          sourceUrl:
            "https://www.reddit.com/r/startups/comments/1cudb5w/where_do_you_track_customer_feedback_without/",
          permalink:
            "https://www.reddit.com/r/startups/comments/1cudb5w/where_do_you_track_customer_feedback_without/",
        },
        {
          id: "ev-fo-2",
          text: "Every week we collect comments, but prioritization still feels like guessing.",
          author: "@buildinpublicops",
          platform: "x",
          sourceLabel: "X",
        },
      ],
    },
    {
      id: "pp-priority-confidence",
      title: "Teams cannot trust prioritization confidence",
      description:
        "Pain points are listed, but teams lack confidence scores and quote-level evidence to defend roadmap choices.",
      severityScore: 83,
      frequency: 137,
      category: "feature_request",
      evidence: [
        {
          id: "ev-pc-1",
          text: "My PM asks why this feature is P1. We have opinions, not evidence.",
          author: "u/productopsmike",
          community: "r/ProductManagement",
          platform: "reddit",
          sourceLabel: "Reddit",
          sourceUrl:
            "https://www.reddit.com/r/ProductManagement/comments/1ctf3uj/how_do_you_defend_prioritization_with_real/",
          permalink:
            "https://www.reddit.com/r/ProductManagement/comments/1ctf3uj/how_do_you_defend_prioritization_with_real/",
        },
        {
          id: "ev-pc-2",
          text: "Leadership keeps asking for confidence scores and proof, but our backlog labels are mostly subjective.",
          author: "u/roadmapoperator",
          community: "r/SaaS",
          platform: "reddit",
          sourceLabel: "Reddit",
          sourceUrl:
            "https://www.reddit.com/r/SaaS/comments/1crx8cm/how_are_you_measuring_confidence_on_feature/",
          permalink:
            "https://www.reddit.com/r/SaaS/comments/1crx8cm/how_are_you_measuring_confidence_on_feature/",
        },
      ],
    },
    {
      id: "pp-time-to-insight",
      title: "Manual synthesis takes too long",
      description:
        "Founders spend hours summarizing threads into docs, and insights are stale by the time actions are decided.",
      severityScore: 76,
      frequency: 112,
      category: "general_rant",
      evidence: [
        {
          id: "ev-ti-1",
          text: "By the time I finish reading and tagging posts, the sprint plan is already locked.",
          author: "@leanloopfounder",
          platform: "x",
          sourceLabel: "X",
        },
        {
          id: "ev-ti-2",
          text: "I lose half a day every Friday trying to summarize community threads into something the team can actually act on.",
          author: "u/customeropsjen",
          community: "r/Entrepreneur",
          platform: "reddit",
          sourceLabel: "Reddit",
          sourceUrl:
            "https://www.reddit.com/r/Entrepreneur/comments/1ctsk8r/how_do_you_turn_community_feedback_into_product/",
          permalink:
            "https://www.reddit.com/r/Entrepreneur/comments/1ctsk8r/how_do_you_turn_community_feedback_into_product/",
        },
      ],
    },
  ],
  recommendations: [
    "Create one evidence-first backlog where each pain point links to source quotes.",
    "Prioritize by frequency x severity so roadmap choices are defendable in planning.",
    "Run weekly insight refreshes to keep customer signals current before sprint kickoff.",
  ],
  generatedIdeas: [
    {
      id: "idea-validation-idea-1",
      title: "Roadmap Confidence Copilot",
      whyNow:
        "Small teams are flooded with fragmented feedback and need faster prioritization cycles without adding PM overhead.",
      fitScore: 92,
      description:
        "AI-powered roadmap prioritization tool that synthesizes customer signals into defendable sprint decisions.",
      problemItSolves:
        "Teams waste hours in prioritization debates without objective evidence to anchor decisions.",
      solutionApproach:
        "Ingests support tickets, community posts, and sales call notes to surface top pain themes with severity weighting.",
      relevanceScore: 88,
      status: "validated" as const,
    },
    {
      id: "idea-validation-idea-2",
      title: "Evidence-Linked Backlog Assistant",
      whyNow:
        "Leaders increasingly ask for proof behind priorities, making traceable quote-to-backlog workflows more valuable.",
      fitScore: 88,
      description:
        "Backlog tool where each feature item links to the underlying customer evidence that prompted it.",
      problemItSolves:
        "Product managers lose visibility between the raw feedback and the Jira tickets it generated.",
      solutionApproach:
        "Maps pain point clusters to feature descriptions with direct quote attribution and severity scores.",
      relevanceScore: 85,
      status: "validated" as const,
    },
    {
      id: "idea-validation-idea-3",
      title: "Weekly Signal Digest for Founders",
      whyNow:
        "Founder-led teams need repeatable insight loops as decision cycles compress and customer channels multiply.",
      fitScore: 84,
      description:
        "Curated weekly report summarising the most important customer pain signals from public communities.",
      problemItSolves:
        "Founders spend half a day manually assembling community feedback into planning documents.",
      solutionApproach:
        "Runs weekly extraction of Reddit/X posts, clusters by theme, and delivers a ranked brief with source links.",
      relevanceScore: 80,
      status: "draft" as const,
    },
  ],
  idealCustomers: [
    {
      id: "idea-validation-customer-1",
      segment: "Founder-led B2B SaaS teams (2-12 people)",
      role: "Founder / Product Lead",
      industry: "B2B SaaS",
      pains: [
        "Feedback spread across support, community, and sales channels",
        "No objective framework to defend roadmap priorities",
      ],
      frustrators: [
        "Manual copy-paste synthesis every week",
        "Re-prioritization based on loudest stakeholder",
      ],
      goals: [
        "Cut weekly synthesis time",
        "Improve roadmap confidence in planning",
      ],
      budget: "$80-$300/mo tooling budget",
      timeline: "Needs value within first 2 weeks",
      urgency: "high",
    },
    {
      id: "idea-validation-customer-2",
      segment: "Early PM teams scaling post-PMF",
      role: "Product Manager",
      industry: "PLG SaaS",
      pains: [
        "Hard to connect qualitative signals to sprint planning",
        "Weak confidence narratives for executive reviews",
      ],
      frustrators: [
        "Subjective prioritization debates",
        "Stale quarterly insight docs",
      ],
      goals: [
        "Standardize evidence-backed decisions",
        "Reduce prioritization churn",
      ],
      budget: "$300-$900/mo team budget",
      timeline: "Decision in current quarter",
      urgency: "medium",
    },
  ],
  landingPageFeaturePrompts: [
    "Show a source-linked evidence timeline proving why each pain point is rising.",
    "Include a confidence meter that explains frequency x severity weighting in plain language.",
    "Highlight a one-click brief generator that maps top pains to launch-ready messaging.",
    "Emphasize weekly refresh automation so teams can keep roadmap decisions current.",
  ],
  validatorContext: {
    score: {
      consistency: 74,
      confidence: "medium",
      severityIndex: 84,
      rationale: [
        "Consistency baseline from report metadata: 74%",
        "Severity index derived from weighted pain-point intensity: 84/100",
        "Confidence is medium based on 3 clustered pain points",
      ],
    },
    derivedAt: "2026-01-15T10:00:00.000Z",
  },
  kind: DEMO_RESULT_KIND.SAMPLE,
};

const GENERATED_IDEAS_BY_NICHE: Record<string, DemoGeneratedIdea[]> = {
  "idea-validation": GUIDED_DEMO_SAMPLE_PREVIEW.generatedIdeas ?? [],
  "saas-analytics": [
    {
      id: "saas-analytics-idea-1",
      title: "Margin Leak Radar",
      whyNow:
        "Operators are pressured by CAC volatility and need clearer visibility into avoidable operational losses.",
      fitScore: 90,
      description:
        "Operational dashboard that surfaces hidden margin erosion across fulfillment, returns, and inventory.",
      problemItSolves:
        "E-commerce teams lack real-time visibility into where margin is silently bleeding across their operations.",
      solutionApproach:
        "Connects to Shopify/WooCommerce APIs to track refunds, returns, and inventory滞销 in one view with alert thresholds.",
      relevanceScore: 87,
      status: "validated" as const,
    },
    {
      id: "saas-analytics-idea-2",
      title: "Retention Recovery Playbook",
      whyNow:
        "Stores are investing heavily in paid channels, so retention-centered optimization has immediate ROI.",
      fitScore: 86,
      description:
        "Lifecycle automation tool that identifies at-risk customers and triggers personalised win-back sequences.",
      problemItSolves:
        "Teams spend heavily on acquisition while churn silently erodes their customer base.",
      solutionApproach:
        "Segments customers by purchase cadence and defect signals, then triggers email/SMS sequences from a template library.",
      relevanceScore: 83,
      status: "draft" as const,
    },
    {
      id: "saas-analytics-idea-3",
      title: "Fulfillment Friction Monitor",
      whyNow:
        "Post-purchase experience is a top churn driver and still monitored manually by many teams.",
      fitScore: 81,
      description:
        "Analytics tool that tracks post-purchase friction signals from unboxing reviews and support tickets.",
      problemItSolves:
        "Operators discover fulfillment problems weeks after they start hurting retention.",
      solutionApproach:
        "Aggregates review data and support tags by order, surfaces friction patterns by carrier and SKU, and alerts ops teams.",
      relevanceScore: 79,
      status: "draft" as const,
    },
  ],
  "email-marketing": [
    {
      id: "email-marketing-idea-1",
      title: "Client Scope Risk Assistant",
      whyNow:
        "Delivery teams need earlier warning when requests drift beyond profitable scope.",
      fitScore: 89,
      description:
        "Project management layer that flags scope creep before it compounds into margin erosion.",
      problemItSolves:
        "Agency teams discover scope creep only at review time, when reversals are expensive.",
      solutionApproach:
        "Tracks task-level estimates vs actuals, flags drift early, and surfaces change-order templates.",
      relevanceScore: 86,
      status: "validated" as const,
    },
    {
      id: "email-marketing-idea-2",
      title: "Proof-of-Progress Snapshot Tool",
      whyNow:
        "Clients expect transparent updates, and teams lose hours preparing ad-hoc reports.",
      fitScore: 85,
      description:
        "Auto-generated project status reports that pull completed milestones from task tools and email into a shareable brief.",
      problemItSolves:
        "Freelancers spend 2-3 hours weekly stitching together status emails from disparate tools.",
      solutionApproach:
        "Connects to Asana/Trello/Linear to pull completed tasks and delivers a weekly visual digest to clients.",
      relevanceScore: 83,
      status: "draft" as const,
    },
    {
      id: "email-marketing-idea-3",
      title: "Revision Pattern Analyzer",
      whyNow:
        "Repeated revision cycles erode margin and indicate unclear value communication.",
      fitScore: 80,
      description:
        "Analytics dashboard that identifies which project types and client types generate the most revision loops.",
      problemItSolves:
        "Teams don't know which engagements are secretly unprofitable until the invoice goes out.",
      solutionApproach:
        "Logs revision requests per project, correlates with scope documents, and surfaces patterns by service type.",
      relevanceScore: 78,
      status: "draft" as const,
    },
  ],
  "dev-tools": [
    {
      id: "dev-tools-idea-1",
      title: "Developer Workflow Bottleneck Tracker",
      whyNow:
        "Engineering teams need measurable proof of where release velocity is really blocked.",
      fitScore: 91,
      description:
        "Analytics layer between CI pipeline and Jira that surfaces which steps cause the most delay.",
      problemItSolves:
        "Engineering leads identify velocity wins but cannot prove which steps are causing the most friction.",
      solutionApproach:
        "Correlates build times, review cycle length, and deploy frequency with ticket attributes to generate bottleneck reports.",
      relevanceScore: 90,
      status: "validated" as const,
    },
    {
      id: "dev-tools-idea-2",
      title: "Reliability Regression Sentinel",
      whyNow:
        "Tooling buyers are prioritizing reliability guarantees over new feature volume.",
      fitScore: 87,
      description:
        "Automated regression detection that runs on every PR and compares error rates against baseline.",
      problemItSolves:
        "Reliability regressions reach production before anyone notices the error rate spike.",
      solutionApproach:
        "Ingest APM traces per PR, compare error budget consumption vs main branch, block merges on regression.",
      relevanceScore: 85,
      status: "validated" as const,
    },
    {
      id: "dev-tools-idea-3",
      title: "Adoption Friction Analyzer",
      whyNow:
        "Strong products fail when onboarding friction hides value from first-week users.",
      fitScore: 83,
      description:
        "Tool that tracks feature adoption rates per user cohort and surfaces which steps users drop off.",
      problemItSolves:
        "Product teams ship features but cannot tell which ones users actually adopt vs ignore.",
      solutionApproach:
        "Instruments product events, clusters by onboarding step completion, and identifies the highest-drop-off steps.",
      relevanceScore: 80,
      status: "draft" as const,
    },
  ],
  "productivity-tools": [
    {
      id: "productivity-tools-idea-1",
      title: "Context Switch Cost Analyzer",
      whyNow:
        "Knowledge workers lose hours daily to app-switching and tool fragmentation.",
      fitScore: 89,
      description:
        "Analytics tool that tracks time lost to context switching across tools and surfaces consolidation opportunities.",
      problemItSolves:
        "Teams don't know how much time they're losing to bouncing between apps.",
      solutionApproach:
        "Instruments window switches, categorizes friction by tool pair, and delivers a consolidation roadmap.",
      relevanceScore: 86,
      status: "validated" as const,
    },
    {
      id: "productivity-tools-idea-2",
      title: "Meeting Fatigue Detector",
      whyNow:
        "Remote teams are burning out from excessive meetings with low output accountability.",
      fitScore: 84,
      description:
        "Dashboard that correlates meeting load with project delivery rates to identify meeting debt.",
      problemItSolves:
        "Managers can't see the hidden cost of meetings on team throughput.",
      solutionApproach:
        "Integrates with calendar APIs, tracks delivery milestones, and surfaces meeting load thresholds.",
      relevanceScore: 82,
      status: "draft" as const,
    },
    {
      id: "productivity-tools-idea-3",
      title: "Deep Work Block Scheduler",
      whyNow:
        "Modern work tools fragment attention and make sustained focus a competitive advantage.",
      fitScore: 81,
      description:
        "Tool that intelligently blocks focus time by analyzing calendar density and task urgency.",
      problemItSolves:
        "Knowledge workers can't protect uninterrupted time for deep work.",
      solutionApproach:
        "Analyzes calendar patterns, respects hard commitments, and auto-blocks focus windows.",
      relevanceScore: 79,
      status: "draft" as const,
    },
  ],
  "ai-writing": [
    {
      id: "ai-writing-idea-1",
      title: "Audience Intent Signal Board",
      whyNow:
        "Creators need clearer monetization signals as platform algorithms shift unpredictably.",
      fitScore: 88,
      description:
        "Dashboard that aggregates engagement signals across YouTube, TikTok, and Substack to show which content drives revenue.",
      problemItSolves:
        "Creators lack a single view of which content actually converts vs which just drives vanity metrics.",
      solutionApproach:
        "Connects creator platform APIs, correlates content topics with revenue events, and surfaces top-converting content types.",
      relevanceScore: 86,
      status: "validated" as const,
    },
    {
      id: "ai-writing-idea-2",
      title: "Offer-Market Fit Evaluator",
      whyNow:
        "Small creator teams need evidence-backed decisions before launching new paid offers.",
      fitScore: 84,
      description:
        "Validation tool that surveys audience sentiment before launching a new product or subscription tier.",
      problemItSolves:
        "Creators launch paid offers into silence because they have no signal on audience readiness.",
      solutionApproach:
        "Runs a 3-question audience pulse, segments by engagement level, and returns a fit score with confidence interval.",
      relevanceScore: 81,
      status: "draft" as const,
    },
    {
      id: "ai-writing-idea-3",
      title: "Content-to-Revenue Gap Tracker",
      whyNow:
        "High output creators often lack reliable visibility into what content converts.",
      fitScore: 82,
      description:
        "Analytics layer that tracks which content pieces lead to paid conversions vs which just get bookmarked.",
      problemItSolves:
        "Creators can't tell which content is driving revenue vs which is just building an audience.",
      solutionApproach:
        "Tags content pieces with revenue events (course signups, affiliate clicks), correlates by topic, and surfaces top converters.",
      relevanceScore: 79,
      status: "draft" as const,
    },
  ],
  "nocode-tools": [
    {
      id: "nocode-tools-idea-1",
      title: "Automation Reliability Guardrails",
      whyNow:
        "Teams adopting AI workflows need trust signals before scaling mission-critical automations.",
      fitScore: 93,
      description:
        "Observability layer that validates AI task outputs against expected error budgets before passing them downstream.",
      problemItSolves:
        "AI automations silently degrade when upstream context shifts, reaching downstream systems with bad outputs.",
      solutionApproach:
        "Monitors task completion rates, flags outlier results, and pauses automation chains before bad outputs propagate.",
      relevanceScore: 92,
      status: "validated" as const,
    },
    {
      id: "nocode-tools-idea-2",
      title: "Prompt Drift Monitor",
      whyNow:
        "Prompt quality drifts over time and quietly degrades outcomes without obvious alerts.",
      fitScore: 87,
      description:
        "Tracking tool that compares prompt output quality over time and alerts when performance drops below baseline.",
      problemItSolves:
        "Teams don't know their prompts are producing worse outputs until customers report it.",
      solutionApproach:
        "Stores output samples per prompt version, tracks quality metrics, and alerts when variance exceeds threshold.",
      relevanceScore: 85,
      status: "draft" as const,
    },
    {
      id: "nocode-tools-idea-3",
      title: "Human-in-the-Loop Handoff Planner",
      whyNow:
        "Companies need practical frameworks to balance automation speed with human QA.",
      fitScore: 85,
      description:
        "Decision framework tool that maps which automation steps need human sign-off vs which can run autonomously.",
      problemItSolves:
        "Teams either over-automate (miss errors) or under-automate (waste human time on trivial checks).",
      solutionApproach:
        "Surveys automation chains, scores each step by risk/frequency, and generates a handoff matrix with confidence thresholds.",
      relevanceScore: 82,
      status: "draft" as const,
    },
  ],
};

const IDEAL_CUSTOMERS_BY_NICHE: Record<string, DemoIdealCustomer[]> = {
  "idea-validation": GUIDED_DEMO_SAMPLE_PREVIEW.idealCustomers ?? [],
  "saas-analytics": [
    {
      id: "saas-analytics-customer-1",
      segment: "DTC operations leads at 7-figure stores",
      role: "Head of Operations",
      industry: "E-commerce",
      pains: [
        "Rising support volume from shipping delays",
        "Low margin visibility by issue type",
      ],
      frustrators: ["Fragmented tooling", "Reactive reporting"],
      goals: ["Protect margin", "Reduce preventable support tickets"],
      budget: "$250-$1,000/mo",
      timeline: "Pilot this month",
      urgency: "high",
    },
    {
      id: "saas-analytics-customer-2",
      segment: "Retention managers at subscription brands",
      role: "Retention Manager",
      industry: "E-commerce",
      pains: [
        "Unclear churn drivers",
        "Inconsistent post-purchase communication",
      ],
      frustrators: ["Siloed customer feedback", "Slow campaign iteration"],
      goals: ["Improve repeat purchase rate", "Launch faster lifecycle tests"],
      budget: "$200-$800/mo",
      timeline: "Quarterly decision",
      urgency: "medium",
    },
  ],
  "productivity-tools": [
    {
      id: "productivity-tools-customer-1",
      segment: "Engineering managers at 20-100 person companies",
      role: "Engineering Manager",
      industry: "Technology",
      pains: ["Excessive meeting load", "Tool fragmentation across teams"],
      frustrators: ["Context switching", "Tool sprawl"],
      goals: ["Protect focused work time", "Reduce meeting overhead"],
      budget: "$200-$800/mo",
      timeline: "Quarterly",
      urgency: "medium",
    },
    {
      id: "productivity-tools-customer-2",
      segment: "Individual knowledge workers",
      role: "Productivity-focused professional",
      industry: "Various",
      pains: ["Context switching between apps", "Lost deep work time"],
      frustrators: ["Slack notifications", "Meeting overload"],
      goals: ["Protect focus time", "Reduce daily friction"],
      budget: "$50-$200/mo",
      timeline: "Immediate",
      urgency: "high",
    },
  ],
  "email-marketing": [
    {
      id: "email-marketing-customer-1",
      segment: "Boutique agency owners",
      role: "Agency Owner",
      industry: "Client Services",
      pains: ["Scope creep", "Team utilization swings"],
      frustrators: ["Manual status updates", "Unstructured feedback loops"],
      goals: ["Protect project margin", "Standardize client delivery"],
      budget: "$100-$500/mo",
      timeline: "Needs proof in first project",
      urgency: "high",
    },
    {
      id: "email-marketing-customer-2",
      segment: "Freelancer collectives",
      role: "Project Lead",
      industry: "Creative Services",
      pains: ["Context loss between contributors", "Delayed approvals"],
      frustrators: ["Version confusion", "Repeated revisions"],
      goals: ["Ship faster", "Reduce rework"],
      budget: "$50-$300/mo",
      timeline: "Immediate",
      urgency: "medium",
    },
  ],
};

const LANDING_PROMPTS_BY_NICHE: Record<string, string[]> = {
  "idea-validation": GUIDED_DEMO_SAMPLE_PREVIEW.landingPageFeaturePrompts ?? [],
  "saas-analytics": [
    "Open with lost-margin urgency and quantify how signal-based prioritization reduces costly ops fires.",
    "Feature a post-purchase pain radar that surfaces repeat complaints before churn spikes.",
    "Highlight customer evidence snapshots that connect issue categories to retention impact.",
  ],
  "productivity-tools": [
    "Lead with the friction of context-switching and app overload.",
    "Show how consolidating tools reduces cognitive load and speeds up delivery.",
    "Highlight evidence linking tool sprawl to burnout and missed deadlines.",
  ],
  "dev-tools": [
    "Position the product as an evidence layer between developer friction and product roadmap choices.",
    "Show feature blocks tied to release reliability, onboarding friction, and time-to-fix metrics.",
    "Include a developer quote strip with linked source snippets for trust.",
  ],
  "email-marketing": [
    "Lead with the cost of hidden scope creep and delayed approvals.",
    "Show a workflow timeline that turns client feedback into prioritized delivery decisions.",
    "Add proof points around reduced revision cycles and healthier margins.",
  ],
  "ai-writing": [
    "Lead with audience monetization uncertainty and frame your tool as a clarity engine.",
    "Map creator pain points to actionable offer and content experiments.",
    "Show social proof emphasizing faster validation before launching paid products.",
  ],
  "nocode-tools": [
    "Open with trust and quality-control risks in AI workflows.",
    "Show guardrail features that tie automation output to confidence signals.",
    "Add a section on human-review triggers for high-risk automation moments.",
  ],
};

export function buildSampleGeneratedIdeas(
  nicheSlug: string,
): DemoGeneratedIdea[] {
  return (
    GENERATED_IDEAS_BY_NICHE[nicheSlug] ??
    GENERATED_IDEAS_BY_NICHE[GUIDED_DEMO_SAMPLE_PREVIEW.nicheSlug] ??
    []
  );
}

export function buildSampleIdealCustomers(
  nicheSlug: string,
): DemoIdealCustomer[] {
  const nicheSpecific = IDEAL_CUSTOMERS_BY_NICHE[nicheSlug];
  if (nicheSpecific && nicheSpecific.length > 0) {
    return nicheSpecific;
  }

  return IDEAL_CUSTOMERS_BY_NICHE[GUIDED_DEMO_SAMPLE_PREVIEW.nicheSlug] ?? [];
}

export function buildSampleLandingPagePrompts(nicheSlug: string): string[] {
  return (
    LANDING_PROMPTS_BY_NICHE[nicheSlug] ??
    LANDING_PROMPTS_BY_NICHE[GUIDED_DEMO_SAMPLE_PREVIEW.nicheSlug] ??
    []
  );
}

export function buildSampleValidatorScore(painPoints: DemoPreviewPainPoint[]) {
  return deriveValidatorScore({
    ...GUIDED_DEMO_SAMPLE_PREVIEW,
    painPoints,
  });
}

function toTitleCaseFromSlug(value: string): string {
  return value
    .trim()
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function toSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export function buildGuidedSamplePreviewForNiche(
  nicheValue: string,
  seedPreview: DemoPreview = GUIDED_DEMO_SAMPLE_PREVIEW,
): DemoPreview {
  const normalizedSlug = toSlug(nicheValue);
  const fallbackSlug = seedPreview.nicheSlug;
  const nicheSlug = normalizedSlug.length > 0 ? normalizedSlug : fallbackSlug;
  const nicheLabel = toTitleCaseFromSlug(nicheSlug);
  const painPoints = seedPreview.painPoints.map((painPoint, index) => ({
    ...painPoint,
    id: `${nicheSlug}-pp-${index + 1}`,
    evidence: painPoint.evidence.map((entry, evidenceIndex) => ({
      ...entry,
      id: `${nicheSlug}-ev-${index + 1}-${evidenceIndex + 1}`,
    })),
  }));

  const validatorScore = deriveValidatorScore({
    ...seedPreview,
    nicheSlug,
    painPoints,
  });

  return {
    ...seedPreview,
    nicheSlug,
    summary: `Across 2,486 public conversations in ${nicheLabel}, people repeatedly struggle to prioritize what to build next with confidence.`,
    recommendations: [
      `Build a ${nicheLabel} insight board where every pain point is linked to source evidence.`,
      `Prioritize ${nicheLabel} opportunities by frequency x severity before planning sprints.`,
      `Review ${nicheLabel} complaints weekly to keep roadmap choices aligned with current demand.`,
    ],
    painPoints,
    // Keep landingPageFeaturePrompts (descriptive prompts, not fabricated report content).
    landingPageFeaturePrompts: buildSampleLandingPagePrompts(nicheSlug),
    // Validator context is legitimately derived from pain points — keep it.
    validatorContext: {
      score: validatorScore,
      derivedAt: "2026-01-15T10:00:00.000Z",
    },
    // generatedIdeas and idealCustomers are intentionally absent —
    // sample-only previews must not fabricate these fields.
  };
}

export interface GuidedDemoProgressStep {
  id: string;
  label: string;
  helper: string;
  progress: number;
}

export const GUIDED_DEMO_PROGRESS_STEPS: GuidedDemoProgressStep[] = [
  {
    id: "collect",
    label: "Collecting posts",
    helper: "We scan public posts for repeated complaints.",
    progress: 22,
  },
  {
    id: "extract",
    label: "Extracting pain points",
    helper: "We group similar problems to find patterns.",
    progress: 52,
  },
  {
    id: "cluster",
    label: "Clustering themes",
    helper: "We rank patterns by severity and frequency.",
    progress: 78,
  },
  {
    id: "synthesize",
    label: "Synthesizing idea and proof",
    helper: "We draft one validated idea and attach evidence.",
    progress: 100,
  },
];
